import * as React from 'react';
import styled from 'styled-components';
import { Modal } from './Modal';
import type { ModalSize, HeaderTextLayout } from './Modal';
import { ModalSheetFooter } from './ModalSheetFooter';
import type { ModalSheetFooterMode } from './ModalSheetFooter';

/**
 * Bare-mode wrapper for legacy content inside ModalSheet.
 *
 * Sets `data-wl-bare` so legacy styled-components (e.g. ModelBody) can
 * detect they're inside the DS modal and switch from hardcoded viewport
 * heights to flex-fill via their own `[data-wl-bare] &` CSS rules.
 *
 * Makes the structural wrapper chain (div#modalContent > div > form >
 * section) flex-column so ModelBody's `flex: 1` propagates correctly.
 * Content inside ModelBody is unaffected — it's a scroll container.
 */
const bareFlexChain = `
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

const BareSlot = styled.div.attrs({ 'data-wl-bare': '' })`
  ${bareFlexChain}

  /*
   * Flex-fill the structural wrapper chain between BareSlot and ModelBody.
   * Legacy modals nest: div#modal*Content > div > form > section > ModelBody.
   * ModelBody handles itself via [data-wl-bare] & { flex:1; overflow-y:auto }.
   * These rules ONLY target known structural wrapper IDs + form/section,
   * NOT content inside ModelBody (which is a scroll container).
   */
  #modalContent,
  #modalContentInner,
  #terminateParticipantModalContent,
  #terminateParticipantModalContentInner {
    ${bareFlexChain}
  }

  form {
    ${bareFlexChain}
  }

  section {
    ${bareFlexChain}
  }
`;

export interface ModalSheetProps {
  /** Controls open/close state */
  open: boolean;
  /** Called when the modal wants to close (overlay click, close button, Escape) */
  onOpenChange: (open: boolean) => void;
  /** Fires after the exit transition completes and the panel is unmounted. */
  onExitComplete?: () => void;
  /** Modal title (rendered inside Modal.Title) */
  title: React.ReactNode;
  /** Optional subtitle below or beside the title */
  subtitle?: React.ReactNode;
  /** Layout direction for title + subtitle: 'vertical' (stacked, default) or 'horizontal' (side by side). */
  headerLayout?: HeaderTextLayout;
  /** Content size -- same as Modal.Content size prop */
  size?: ModalSize;
  /** Semantic ID for test automation -- auto-generates Close semanticId as `${semanticId}Close` */
  semanticId: string;
  /** Shows spinner inside Modal.Body when true */
  loading?: boolean;
  /** Allow closing by clicking the overlay (default: false) */
  closeOnOverlayClick?: boolean;
  /** Custom footer ReactNode. Takes priority over footerMode. */
  footer?: React.ReactNode;
  /**
   * When true, children render directly inside Modal.Content after the header
   * without Modal.Body / Modal.Footer wrapping. Use for hybrid modals where
   * legacy content provides its own body padding, scroll, and footer.
   */
  bare?: boolean;
  /** Modal body content */
  children: React.ReactNode;

  /* ── Footer preset props ────────────────────────────────────────── */

  /**
   * Operational mode controlling modal footer and behavior.
   * Alias for `footerMode` — preferred in new code.
   */
  mode?: ModalSheetFooterMode;
  /** @deprecated Use `mode` instead. Auto-generates footer from preset. */
  footerMode?: ModalSheetFooterMode;
  /** Dirty guard — applies to Cancel button AND Close X button. */
  dirty?: boolean;
  /** Message for dirty guard confirm dialog. */
  confirmMessage?: string;
  /** Loading state for the primary action button. */
  actionLoading?: boolean;
  /** Disabled state for the primary action button. */
  actionDisabled?: boolean;
  /** Handler for the primary action (view: Edit click, edit/add: form submit override, approver: Approve). */
  onAction?: () => void;
  /** Handler for secondary action (approver mode: Reject). */
  onSecondaryAction?: () => void;
  /** Override the auto-resolved action label. */
  actionLabel?: string;
  /** Override the auto-resolved action icon. */
  actionIcon?: string;
  /** View mode: show or hide the action button. Default: true */
  showAction?: boolean;
  /** Override cancel label. */
  cancelLabel?: string;
  /** Override cancel handler (default: close modal). */
  onCancel?: () => void;
  /**
   * Associates the action button with a `<form>` by id (native `form` attribute).
   * Enables form submission from the DS footer when the form lives inside bare content.
   */
  actionForm?: string;
}

/**
 * Pre-composed modal for the 90% case: title + body + optional footer.
 *
 * Footer resolution priority: `footer` > `footerMode` > no footer.
 * Works in both normal and `bare` mode.
 *
 * Pass `bare` when wrapping legacy content that provides its own body
 * padding and scroll. Use `footerMode` + `actionForm` to render a DS footer
 * that submits a form inside the bare content.
 */
export const ModalSheet = React.forwardRef<HTMLDivElement, ModalSheetProps>(function ModalSheet(
  {
    open,
    onOpenChange,
    onExitComplete,
    title,
    subtitle,
    headerLayout = 'vertical',
    size = 'md',
    semanticId,
    loading,
    closeOnOverlayClick,
    footer,
    bare = false,
    children,
    mode,
    footerMode: footerModeProp,
    dirty,
    confirmMessage,
    actionLoading,
    actionDisabled,
    onAction,
    onSecondaryAction,
    actionLabel,
    actionIcon,
    showAction,
    cancelLabel,
    onCancel,
    actionForm,
  },
  ref
) {
  const resolvedFooterMode = mode ?? footerModeProp;
  const hasDirtyGuard = dirty === true;

  const resolvedFooter = React.useMemo(() => {
    if (footer != null) return <Modal.Footer>{footer}</Modal.Footer>;
    if (resolvedFooterMode != null) {
      return (
        <ModalSheetFooter
          mode={resolvedFooterMode}
          dirty={dirty}
          confirmMessage={confirmMessage}
          actionLoading={actionLoading}
          actionDisabled={actionDisabled}
          onAction={onAction}
          onSecondaryAction={onSecondaryAction}
          actionLabel={actionLabel}
          actionIcon={actionIcon}
          showAction={showAction}
          cancelLabel={cancelLabel}
          onCancel={onCancel}
          semanticId={semanticId}
          actionForm={actionForm}
        />
      );
    }
    return null;
  }, [
    footer,
    resolvedFooterMode,
    dirty,
    confirmMessage,
    actionLoading,
    actionDisabled,
    onAction,
    onSecondaryAction,
    actionLabel,
    actionIcon,
    showAction,
    cancelLabel,
    onCancel,
    semanticId,
    actionForm,
  ]);

  return (
    <Modal.Root open={open} onOpenChange={onOpenChange} onExitComplete={onExitComplete}>
      <Modal.Portal>
        <Modal.Overlay closeOnOverlayClick={closeOnOverlayClick} />
        <Modal.Content ref={ref} size={size} semanticId={semanticId}>
          <Modal.Header>
            <Modal.HeaderText layout={headerLayout}>
              <Modal.Title>{title}</Modal.Title>
              {subtitle != null && <Modal.Subtitle>{subtitle}</Modal.Subtitle>}
            </Modal.HeaderText>
            <Modal.Close
              semanticId={`${semanticId}Close`}
              dirty={hasDirtyGuard}
              confirmMessage={confirmMessage}
            />
          </Modal.Header>
          {bare ? (
            loading ? (
              <Modal.Body loading />
            ) : (
              <>
                <BareSlot>{children}</BareSlot>
                {resolvedFooter}
              </>
            )
          ) : (
            <>
              <Modal.Body loading={loading}>{children}</Modal.Body>
              {!loading && resolvedFooter}
            </>
          )}
        </Modal.Content>
      </Modal.Portal>
    </Modal.Root>
  );
});
